import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import emailjs from '@emailjs/browser';

// ── Data ──────────────────────────────────────────────────────────────────────

const contactReasons = [
  {
    icon: '🏥',
    title: 'HealHub Demo',
    desc: 'Book a free demo of HealHub clinic management software.',
    color: '#22C55E',
  },
  {
    icon: '🛡️',
    title: 'Suraksha Waitlist',
    desc: 'Join the early access waitlist for Suraksha anti-fraud app.',
    color: '#E63946',
  },
  {
    icon: '🚀',
    title: 'Start a Project',
    desc: 'Discuss a custom software development or AI integration project.',
    color: '#3B82F6',
  },
  {
    icon: '👥',
    title: 'Hire Developers',
    desc: 'Embed pre-vetted engineers into your team within 48 hours.',
    color: '#8B5CF6',
  },
  {
    icon: '🤖',
    title: 'Vedant Pilot',
    desc: 'Apply to be a pilot clinic for the Vedant AI receptionist.',
    color: '#A855F7',
  },
  {
    icon: '💼',
    title: 'Partnership',
    desc: 'Explore reseller, integration, or strategic partnership opportunities.',
    color: '#F59E0B',
  },
];

const officeDetails = [
  {
    icon: '📍',
    label: 'Address',
    value: 'Dev Heights, First Floor, Aman Vihar, Sahastradhara Road, Dehradun — 248001, Uttarakhand, India',
  },
  {
    icon: '📧',
    label: 'Email',
    value: 'a.uniyal@redcubicalsystems.com',
    href: 'mailto:a.uniyal@redcubicalsystems.com',
  },
  {
    icon: '📞',
    label: 'Phone',
    value: '+91 9368487416',
    href: 'tel:+919368487416',
  },
  {
    icon: '⏰',
    label: 'Response Time',
    value: 'Within 24 hours — every enquiry personally reviewed',
  },
];

const faqs = [
  {
    question: 'How quickly do you respond to enquiries?',
    answer: 'Every message is personally reviewed by Ashish. You will hear back within 24 hours — not from an automated system, but from the founder directly.',
  },
  {
    question: 'I am a clinic owner. How do I get started with HealHub?',
    answer: 'Fill in the form and select "HealHub Demo" as your reason for contact. Our team will schedule a 30-minute live walkthrough tailored to your clinic type. No commitment required.',
  },
  {
    question: 'I have a software project. Do you take client work?',
    answer: 'Yes — we take 3 to 4 carefully selected client projects per quarter. We focus on complex, meaningful problems in healthcare, fintech, AI, and enterprise software. Fill in the form and tell us about your project.',
  },
  {
    question: 'Can I hire a developer from your team?',
    answer: 'Absolutely. We provide staff augmentation — pre-vetted backend, frontend, mobile, AI, and DevOps engineers who embed directly in your team. Typically onboarded within 48 hours of agreement.',
  },
  {
    question: 'Where are you located? Do you work with clients outside Dehradun?',
    answer: 'We are headquartered in Dehradun, Uttarakhand. We work with clients across India and internationally. Time zone friendly for US and EU clients.',
  },
  {
    question: 'Is Suraksha available to download now?',
    answer: 'Suraksha is currently in advanced development and launching soon on Android and iOS. Join the waitlist to be among the first protected at launch.',
  },
];

// ── Contact Form ──────────────────────────────────────────────────────────────

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    reason: 'Start a Project',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await emailjs.send(
        'service_g9tvszg',
        'template_124o1la',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          company: formData.company || 'Not provided',
          message: `Reason: ${formData.reason}\n\n${formData.message}`,
          to_name: 'Ashish Uniyal',
        },
        'u6gb-CCFGNTwlW3Ot'
      );
      setSubmitted(true);
    } catch {
      setError(
        'Something went wrong. Please email us directly at a.uniyal@redcubicalsystems.com'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  const selectArrow = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`;

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '64px 32px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '20px',
      }}>
        <p style={{ fontSize: '56px', marginBottom: '20px' }}>✅</p>
        <h3 style={{
          fontFamily: 'Barlow Condensed, sans-serif',
          fontSize: '32px', fontWeight: '900',
          color: '#fff', textTransform: 'uppercase',
          marginBottom: '12px',
        }}>
          Message Sent!
        </h3>
        <p style={{
          fontSize: '15px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: '1.7',
          maxWidth: '400px',
          margin: '0 auto 24px',
        }}>
          Ashish will personally review your message and
          respond within 24 hours. No automated replies —
          a real conversation awaits.
        </p>
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          gap: '8px',
          background: 'rgba(34,197,94,0.1)',
          border: '1px solid rgba(34,197,94,0.25)',
          borderRadius: '50px', padding: '8px 20px',
        }}>
          <span style={{
            width: '8px', height: '8px',
            borderRadius: '50%', background: '#22C55E',
          }} />
          <span style={{
            fontSize: '13px', fontWeight: '600',
            color: '#22C55E',
          }}>
            Response expected within 24 hours
          </span>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      {/* Row 1 — Name + Email */}
      <div className="contact-form-row">
        <div>
          <label style={labelStyle}>Your Name *</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full name"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e =>
              (e.target.style.borderColor = 'rgba(59,130,246,0.2)')
            }
          />
        </div>
        <div>
          <label style={labelStyle}>Email *</label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@company.com"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e =>
              (e.target.style.borderColor = 'rgba(59,130,246,0.2)')
            }
          />
        </div>
      </div>

      {/* Row 2 — Phone + Company */}
      <div className="contact-form-row">
        <div>
          <label style={labelStyle}>Phone</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e =>
              (e.target.style.borderColor = 'rgba(59,130,246,0.2)')
            }
          />
        </div>
        <div>
          <label style={labelStyle}>Company / Clinic</label>
          <input
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your organisation name"
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e =>
              (e.target.style.borderColor = 'rgba(59,130,246,0.2)')
            }
          />
        </div>
      </div>

      {/* Reason */}
      <div>
        <label style={labelStyle}>Reason for Contact</label>
        <select
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          style={{
            ...inputStyle,
            cursor: 'pointer',
            appearance: 'none',
            backgroundImage: selectArrow,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 16px center',
            paddingRight: '40px',
          }}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e =>
            (e.target.style.borderColor = 'rgba(59,130,246,0.2)')
          }
        >
          <option value="HealHub Demo" style={{ background: '#0A0E1A' }}>
            🏥 HealHub Demo
          </option>
          <option value="Suraksha Waitlist" style={{ background: '#0A0E1A' }}>
            🛡️ Suraksha Waitlist
          </option>
          <option value="Start a Project" style={{ background: '#0A0E1A' }}>
            🚀 Start a Software Project
          </option>
          <option value="Hire Developers" style={{ background: '#0A0E1A' }}>
            👥 Hire Developers
          </option>
          <option value="Vedant Pilot" style={{ background: '#0A0E1A' }}>
            🤖 Vedant Pilot Program
          </option>
          <option value="Partnership" style={{ background: '#0A0E1A' }}>
            💼 Partnership Enquiry
          </option>
          <option value="Other" style={{ background: '#0A0E1A' }}>
            💬 Something Else
          </option>
        </select>
      </div>

      {/* Message */}
      <div>
        <label style={labelStyle}>Your Message *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us what you need. The more detail you share, the better we can help."
          required
          rows={5}
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '130px',
          }}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e =>
            (e.target.style.borderColor = 'rgba(59,130,246,0.2)')
          }
        />
      </div>

      {error && (
        <p style={{
          fontSize: '13px', color: '#E63946',
          background: 'rgba(230,57,70,0.08)',
          border: '1px solid rgba(230,57,70,0.2)',
          padding: '10px 14px', borderRadius: '8px',
        }}>
          ⚠️ {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary"
        style={{
          width: '100%',
          justifyContent: 'center',
          padding: '18px',
          fontSize: '15px',
          opacity: submitting ? 0.7 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? '⏳ Sending...' : 'Send Message to Ashish ↗'}
      </button>

      <p style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.25)',
        textAlign: 'center',
        lineHeight: '1.6',
      }}>
        We respond within 24 hours ·
        Your data is never shared or sold
      </p>
    </form>
  );
}

// ── FAQ Item ──────────────────────────────────────────────────────────────────

function FAQItem({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '24px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          textAlign: 'left',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <span style={{
          fontSize: '15px',
          fontWeight: '600',
          color: open ? '#fff' : 'rgba(255,255,255,0.7)',
          lineHeight: '1.5',
          transition: 'color 0.2s',
        }}>
          {question}
        </span>
        <span style={{
          fontSize: '20px',
          color: open ? '#3B82F6' : 'rgba(255,255,255,0.3)',
          flexShrink: 0,
          transition: 'all 0.3s',
          transform: open ? 'rotate(45deg)' : 'none',
          display: 'inline-block',
          lineHeight: 1,
        }}>
          +
        </span>
      </button>

      {open && (
        <div style={{
          paddingBottom: '24px',
          animation: 'faqFadeIn 0.2s ease-out',
        }}>
          <p style={{
            fontSize: '15px',
            color: 'rgba(255,255,255,0.5)',
            lineHeight: '1.75',
          }}>
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Contact() {
  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="Contact Us — Redcubical Systems"
        description="Get in touch with Redcubical Systems. Book a HealHub demo, join the Suraksha waitlist, start a software project, or hire developers. Based in Dehradun. Responding within 24 hours."
        canonical="/contact"
        keywords="contact Redcubical Systems, HealHub demo, hire developers Dehradun, software company contact, Suraksha waitlist, Ashish Uniyal contact"
      />

      {/* ── HERO ── */}
      <section
        style={{
          padding: '100px 32px 80px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(37,99,235,0.4) 0%,
              rgba(29,58,138,0.2) 40%,
              transparent 70%),
            var(--black)
          `,
        }}
      >
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          width: '100%', position: 'relative',
          textAlign: 'center',
        }}>
          <span className="badge" style={{ marginBottom: '24px' }}>
            💬 &nbsp; We Respond Within 24 Hours
          </span>

          <h1 className="display-heading-md" style={{ marginBottom: '20px' }}>
            LET'S START A<br />
            <span className="gradient-text">CONVERSATION</span>
          </h1>

          <p style={{
            fontSize: 'clamp(16px, 2vw, 19px)',
            color: 'rgba(255,255,255,0.55)',
            lineHeight: '1.8',
            maxWidth: '560px',
            margin: '0 auto',
          }}>
            Whether you are a clinic owner, a startup founder, or someone
            who needs to talk to a human — you have come to the right place.
            Every message is personally read by Ashish.
          </p>
        </div>
      </section>

      {/* ── REASONS TO CONTACT ── */}
      <section style={{
        padding: '64px 32px',
        background: 'var(--dark)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <p style={{
            fontSize: '11px', fontWeight: '700',
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: '3px', textTransform: 'uppercase',
            textAlign: 'center', marginBottom: '32px',
          }}>
            What can we help you with?
          </p>

          <div style={{
            display: 'grid', gap: '12px',
          }} className="reasons-grid">
            {contactReasons.map((reason, i) => (
              <div
                key={i}
                style={{
                  padding: '24px',
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${reason.color}20`,
                  borderRadius: '16px',
                  transition: 'all 0.25s',
                  cursor: 'default',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = `${reason.color}08`;
                  e.currentTarget.style.borderColor = `${reason.color}40`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = `${reason.color}20`;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: '28px', display: 'block', marginBottom: '12px' }}>
                  {reason.icon}
                </span>
                <p style={{
                  fontSize: '14px', fontWeight: '700',
                  color: '#fff', marginBottom: '6px',
                }}>
                  {reason.title}
                </p>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.4)',
                  lineHeight: '1.5',
                }}>
                  {reason.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAIN CONTACT SECTION ── */}
      <section style={{
        padding: '80px 32px 120px',
        background: 'var(--dark)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="contact-main-grid">

            {/* Left — Info */}
            <div>
              {/* Office details */}
              <div style={{ marginBottom: '48px' }}>
                <span className="section-label" style={{ marginBottom: '24px' }}>
                  Get In Touch
                </span>
                <h2 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(32px, 5vw, 52px)',
                  fontWeight: '900', color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1.05, marginBottom: '32px',
                }}>
                  TALK TO US.<br />
                  <span className="gradient-text">NOT A CHATBOT.</span>
                </h2>

                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '20px',
                }}>
                  {officeDetails.map((detail) => (
                    <div key={detail.label} style={{
                      display: 'flex', gap: '16px',
                      alignItems: 'flex-start',
                    }}>
                      <div style={{
                        width: '44px', height: '44px',
                        background: 'rgba(59,130,246,0.1)',
                        border: '1px solid rgba(59,130,246,0.2)',
                        borderRadius: '10px',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px', flexShrink: 0,
                      }}>
                        {detail.icon}
                      </div>
                      <div>
                        <p style={{
                          fontSize: '11px', fontWeight: '700',
                          color: 'rgba(255,255,255,0.3)',
                          letterSpacing: '1.5px', textTransform: 'uppercase',
                          marginBottom: '4px',
                        }}>
                          {detail.label}
                        </p>
                        {detail.href ? (
                          <a
                            href={detail.href}
                            style={{
                              fontSize: '14px',
                              color: '#3B82F6',
                              textDecoration: 'none',
                              fontWeight: '500',
                              lineHeight: '1.5',
                              transition: 'color 0.2s',
                            }}
                            onMouseEnter={e =>
                              (e.currentTarget.style.color = '#60A5FA')
                            }
                            onMouseLeave={e =>
                              (e.currentTarget.style.color = '#3B82F6')
                            }
                          >
                            {detail.value}
                          </a>
                        ) : (
                          <p style={{
                            fontSize: '14px',
                            color: 'rgba(255,255,255,0.6)',
                            lineHeight: '1.5',
                          }}>
                            {detail.value}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Founder direct access */}
              <div style={{
                padding: '32px',
                background: 'rgba(59,130,246,0.06)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: '20px',
                marginBottom: '32px',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  gap: '16px', marginBottom: '16px',
                }}>
                  <div style={{
                    width: '52px', height: '52px',
                    borderRadius: '50%',
                    background: 'rgba(59,130,246,0.15)',
                    border: '2px solid rgba(59,130,246,0.3)',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '18px', fontWeight: '900',
                    color: '#3B82F6',
                  }}>
                    AU
                  </div>
                  <div>
                    <p style={{
                      fontSize: '15px', fontWeight: '700', color: '#fff',
                    }}>
                      Ashish Uniyal
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.4)',
                    }}>
                      Founder & CEO · Redcubical Systems
                    </p>
                  </div>
                </div>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.55)',
                  lineHeight: '1.7',
                  fontStyle: 'italic',
                }}>
                  "Skip the sales funnel. Send me a message directly.
                  If your problem is interesting, I want to be the
                  first person you talk to about it."
                </p>
              </div>

              {/* Map placeholder */}
              <div style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.12)',
                borderRadius: '16px',
                padding: '40px 32px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  backgroundImage: `
                    linear-gradient(rgba(59,130,246,0.04) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(59,130,246,0.04) 1px, transparent 1px)
                  `,
                  backgroundSize: '28px 28px',
                }} />
                <div style={{ position: 'relative' }}>
                  <p style={{ fontSize: '36px', marginBottom: '12px' }}>📍</p>
                  <p style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '20px', fontWeight: '900',
                    color: '#fff', textTransform: 'uppercase',
                    marginBottom: '4px',
                  }}>
                    Dehradun, Uttarakhand
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.35)',
                    marginBottom: '20px',
                  }}>
                    248001 · India
                  </p>
                  <a
                    href="https://maps.google.com/?q=Aman+Vihar+Sahastradhara+Road+Dehradun+Uttarakhand"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center', gap: '8px',
                      fontSize: '13px', fontWeight: '600',
                      color: '#3B82F6', textDecoration: 'none',
                      border: '1px solid rgba(59,130,246,0.3)',
                      padding: '8px 20px', borderRadius: '50px',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(59,130,246,0.1)';
                      e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)';
                    }}
                  >
                    View on Google Maps ↗
                  </a>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '24px',
              padding: '48px 44px',
            }}>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: '28px', fontWeight: '900',
                color: '#fff', textTransform: 'uppercase',
                marginBottom: '8px',
              }}>
                Send a Message
              </p>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.4)',
                marginBottom: '32px',
                lineHeight: '1.6',
              }}>
                All enquiries welcome. We respond to every message
                personally within 24 hours.
              </p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="faq-grid">

            {/* Left — Header */}
            <div style={{ position: 'sticky', top: '100px' }}>
              <span className="section-label">FAQ</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                QUESTIONS<br />
                <span className="gradient-text">WE GET ASKED</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px' }}>
                Cannot find what you are looking for?
                Send us a message — we respond to every enquiry personally.
              </p>
              <a
                href="mailto:a.uniyal@redcubicalsystems.com"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center', gap: '8px',
                  fontSize: '14px', fontWeight: '600',
                  color: '#3B82F6', textDecoration: 'none',
                  border: '1px solid rgba(59,130,246,0.3)',
                  padding: '12px 24px', borderRadius: '50px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(59,130,246,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)';
                }}
              >
                Email Us Directly ↗
              </a>
            </div>

            {/* Right — FAQ Items */}
            <div>
              {faqs.map((faq, i) => (
                <FAQItem
                  key={i}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── EMERGENCY CYBER FRAUD BANNER ── */}
      <section style={{
        padding: '48px 32px',
        background: 'var(--black)',
        borderTop: '1px solid rgba(230,57,70,0.15)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{
            background: 'rgba(230,57,70,0.06)',
            border: '1px solid rgba(230,57,70,0.2)',
            borderRadius: '20px',
            padding: '32px 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '20px',
          }}>
            <div>
              <p style={{
                fontSize: '16px', fontWeight: '700',
                color: '#fff', marginBottom: '6px',
              }}>
                🚨 Experiencing cyber fraud right now?
              </p>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: '1.6',
              }}>
                Do not contact us — call the National Cybercrime Helpline immediately.
                Available 24/7, free of charge.
              </p>
            </div>
            <a
              href="tel:1930"
              style={{
                display: 'inline-flex',
                alignItems: 'center', gap: '10px',
                background: '#E63946',
                color: '#fff',
                padding: '16px 32px',
                borderRadius: '50px',
                fontSize: '18px',
                fontWeight: '900',
                fontFamily: 'Barlow Condensed, sans-serif',
                textDecoration: 'none',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                flexShrink: 0,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#C1121F';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#E63946';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              📞 Call 1930
            </a>
          </div>
        </div>
      </section>

      {/* ── RESPONSIVE + ANIMATIONS ── */}
      <style>{`
        @keyframes faqFadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .reasons-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .contact-main-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .faq-grid {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 80px;
          align-items: start;
        }
        .contact-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 1024px) {
          .faq-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
        }
        @media (max-width: 768px) {
          .reasons-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .contact-main-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .contact-form-row {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 480px) {
          .reasons-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}